import React, { useState, useEffect } from 'react'
import { apiDelete, apiGet, apiPost, apiPut } from '../../generic/Api_Functions'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import AlertUI from '../../generic/AlertUI'
import AddElementPart from '../../generic/smartAddElement/AddElementPart'
import { Typography, TextField, MenuItem, FormHelperText, IconButton, Button } from '@mui/material'
import WorkHistoryIcon from '@mui/icons-material/WorkHistory'
import SquareFootIcon from '@mui/icons-material/SquareFoot'
import PartFeature from './PartFeature'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { PartHistory } from './partHistory'
import { PauseCircle, TextSnippetOutlined, Tune } from '@mui/icons-material'
import PartPreset from './PartPreset'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import { useToggle } from '../../generic/hooks/useToggle'
import PauseResumeFinishedList from './pause/PauseResumeFinishedList'
import PausePartSelectEndJobDialog from './pause/PausePartSelectEndJobDialog'
import Document from '../document/Document'
import { DefaultAccordion } from '../../generic/DefaultAccordion'
import theme from '../../../themeConfig'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import QrCodeGeneratorDialog from '../../generic/qr/QrCodeGeneratorDialog'
import { PART_RESULT_OK, PART_RESULT_NOK, partResultLabel } from '../../generic/utilities/partResultLabel'

const Parts = (props) => {
  const [AlertElement, showAlert] = AlertUI()
  const {
    partReferenceFilter = null,
    partReference = null,
    hideColumns = null,
    setEditingRow,
    setSelectedPart,
    forceLoad = false,
    fabricationOrder = null,
    hideAddRecord = false,
    compatibleFixtureCodes = [],
    showQrAction = false,
    // Historia 170 (J4b, S103) — additive, optional, inert when absent (every OTHER caller):
    // `ReferenceTable.jsx` forwards this when a document repository row's "Open associated record"
    // preselects a PART inside this reference (`generic/SmartMateriaUITable.jsx`'s own prop, same
    // shape).
    preselectedRecord = null
  } = props
  const parallelepiped = 'PARALLELEPIPED'
  const cylindrical = 'CYLINDRICAL'
  const [currentEditingRow, setCurrentEditingRow] = useState(null)
  const [editingRawMaterialId, setEditingRawMaterialId] = useState(null)
  const [availableRawMaterials, setAvailableRawMaterials] = useState([])
  const [reloadAvailableMaterials, setReloadAvailableMaterials] = useState(false)
  // RN1/INV2: the catalogue offered while editing a row is the same 7 values Sun publishes, none
  // split in two. The FINISHED_OK/FINISHED_NOK translation layer this used to share with the
  // creation dialog (generic/utilities/partFinishedStatus) is retired entirely.
  const [partStatusOptions, setPartStatusOptions] = useState([])
  // Mirrors the status/result currently chosen for the row being edited. Kept apart from the row
  // object itself (which is mutated directly, same idiom as editingRawMaterialId) so that changing
  // either one re-renders Parts and, through it, the columns passed to SmartMateriaUITable (RN7/RN12).
  const [editingStatus, setEditingStatus] = useState(null)
  const [editingResult, setEditingResult] = useState('')
  const [selectedRow, setSelectedRow] = useState([])
  const [showQr, setShowQr] = useState(false)
  const [showPartHistory, setShowPartHistory] = useState(false)
  const [showPartFeatures, setShowPartFeatures] = useState(false)
  const [showPreset, setShowPreset] = useState(false)
  const [showDocument, setShowDocument] = useState(false)
  const { t_ } = useSmartTranslation()
  const [resetMultipleSelectionValue, toogleResetMultipleSelectionValue] = useToggle()

  const [unfinishedPartsPauseResume, setUnfinishedPartsPauseResume] = useState([])
  const [partsOnDevicesToPause, setPartsOnDevicesToPause] = useState([])
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const [selectedJob, setSelectedJob] = useState(null)
  const [showJobDocument, setShowJobDocument] = useState(false)

  const isUserAuthorized = useAuthorization()

  const accordionStyle = {
    minWidth: '80rem',
    width: '100%',
    display: 'grid',
    gap: '1rem',
    backgroundColor: theme.palette.background.row2,
    padding: '1rem'
  }

  const [filters, setFilters] = useState([
    {
      // S38/RN15.3: labelled 'Result', no longer 'QUALITY'. valueName/field are left untouched on
      // purpose -- they are the wire identifiers Sun still reads with today's simple isOk check
      // (RN15/D13, the compound result filter, is Sun's own pending work: S41-S45). Renaming them
      // without evidence of what that work expects would risk breaking the filter -- and S61/S62
      // have since fixed on Sun's side that the wire identifier is the FIELD, never the label.
      // S68 closes the half S38 left open: the two indicators of this control ARE the filter's two
      // categories (ratified by the PO), so the chip keeps its boolean shape and the missing piece
      // was the assertion, not a change of shape. Labels are not part of the contract: the two
      // categories are icons, not the literals "OK"/"NOK".
      name: t_('Result'),
      valueName: 'QUALITY',
      key: 'NONE',
      field: 'isOk'
    },
    {
      name: t_('REWORKED'),
      valueName: 'REWORKED',
      key: 'NONE',
      field: 'isReworked'
    },
    {
      name: t_('FINISHED'),
      valueName: 'FINISHED',
      key: 'NONE',
      field: 'status'
    },
    {
      name: t_('DELIVERED'),
      valueName: 'DELIVERED',
      key: 'NONE',
      field: 'status'
    },
    {
      name: t_('SCRAP'),
      valueName: 'SCRAP',
      key: 'NONE',
      field: 'status'
    }
  ])

  const getTableData = async (pagination) => {
    let parts
    if (fabricationOrder === null || fabricationOrder === undefined) {
      parts = await apiPost('partsId/' + partReference.id, pagination)
    } else {
      parts = await apiPost('partsId/' + partReference.id + '/' + fabricationOrder, pagination)
    }

    if (compatibleFixtureCodes.length > 0) {
      parts = { ...parts, content: parts.content.map(part => ({ ...part, disabled: !compatibleFixtureCodes.includes(part.fixtureReference != null ? part.fixtureReference.code : part.partReference?.defaultFixture?.code) })) }
    }
    return parts
  }

  useEffect(() => {
    const loadAvailableRawMaterials = async () => {
      const data = await apiGet('rawMaterialsAvailable')
      setAvailableRawMaterials(data)
    }
    loadAvailableRawMaterials()
  }, [reloadAvailableMaterials])

  useEffect(() => {
    const loadPartStatusOptions = async () => {
      const data = await apiGet('partStatuses')
      setPartStatusOptions(data || [])
    }
    loadPartStatusOptions()
  }, [])

  const checkDimensionCondition = (row, column) => {
    let errorMsg = ''
    if ((row.rawMaterialReference !== null && row.rawMaterialReference !== undefined) && (Object.keys(row.rawMaterialReference).length > 0) &&
      (Object.keys(row.partReference).length > 0)) {
      if (row.partReference.shape.code === row.rawMaterialReference.shape.code) {
        if (row.partReference.shape.code === cylindrical) {
          if (row.partReference.diameter > row.rawMaterialReference.diameter || row.partReference.length > row.rawMaterialReference.length) {
            if (column === 'rawMaterialReference') {
              errorMsg = t_('Raw material reference should be bigger')
            } else if (column === 'partReference') {
              errorMsg = t_('Part reference should be smaller')
            }
          }
        } else if (row.partReference.shape.code === parallelepiped) {
          if (row.partReference.lengthX > row.rawMaterialReference.lengthX || row.partReference.lengthY > row.rawMaterialReference.lengthY || row.partReference.lengthZ > row.rawMaterialReference.lengthZ) {
            if (column === 'rawMaterialReference') {
              errorMsg = t_('Raw material reference should be bigger')
            } else if (column === 'partReference') {
              errorMsg = t_('Part reference should be smaller')
            }
          }
        }
      }
    }
    return errorMsg
  }

  const columns = [
    {
      name: t_('Status'),
      field: 'status',
      mandatory: true,
      isSimpleData: true,
      // RN1/INV2: the catalogue offered here is the same 7 values Sun publishes, none split into
      // FINISHED_OK/FINISHED_NOK. RN7/RN12: whether the row is FINISHED, and what its Result is,
      // are two independent choices, so this column owns a small custom editor (same idiom as the
      // "Raw Material" column below) instead of the table's generic smartSelect editor: only a
      // fully custom render can show the Result dropdown next to Status, only for the row being
      // edited, only while it is FINISHED, and only as mandatory in that case (S22-S24).
      render: (row) => {
        const isEditingThisRow = currentEditingRow !== null && currentEditingRow.id === row.id
        if (!isEditingThisRow) {
          return row.status
        }
        const showResult = editingStatus === 'FINISHED'
        const resultMissing = showResult && (editingResult === null || editingResult === '')
        // RN19.1/INV6.1 (S63/S66): the dropdown shows the status held in `editingStatus`, seeded
        // from the part's PERSISTED status -- never `row.status`, which for a paused or forecast
        // part carries a pseudo-state that is not a member of the catalogue, so a native select
        // silently fell back to its first option (defect O3).
        return (
          <>
            <TextField
              select
              variant='outlined'
              style={{ minWidth: '8em' }}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true }}
              inputProps={{ 'data-testid': 'edit-status-select' }}
              value={editingStatus || ''}
              onChange={(event) => {
                const newStatus = event.target.value
                setEditingStatus(newStatus)
                // RN12/S23: leaving FINISHED retires the result instead of leaving it stuck. RN7:
                // entering FINISHED from elsewhere starts empty, never auto-preselected (S24) —
                // P1's OK preselection is specific to the creation dialog (CU1), not to editing.
                if (newStatus !== 'FINISHED') {
                  setEditingResult('')
                }
              }}
            >
              {partStatusOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </TextField>
            <FormHelperText> </FormHelperText>
            {showResult && (
              <>
                <TextField
                  select
                  variant='outlined'
                  style={{ minWidth: '6em', marginTop: '0.5em' }}
                  label={t_('Result')}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ native: true }}
                  inputProps={{ 'data-testid': 'edit-result-select' }}
                  error={resultMissing}
                  value={editingResult}
                  onChange={(event) => setEditingResult(event.target.value)}
                >
                  <option value='' />
                  <option value={PART_RESULT_OK}>{partResultLabel(PART_RESULT_OK, t_)}</option>
                  <option value={PART_RESULT_NOK}>{partResultLabel(PART_RESULT_NOK, t_)}</option>
                </TextField>
                <FormHelperText error={resultMissing}>{resultMissing ? t_('Result is required') : ' '}</FormHelperText>
              </>
            )}
          </>
        )
      }
    },
    {
      name: t_('Code'),
      field: 'code',
      charLimit: 60,
      mandatory: true,
      unique: true,
      readOnly: true
    },
    {
      name: t_('Name'),
      field: 'name',
      charLimit: 70,
      mandatory: true
    },
    {
      // S32/S33/S34 (P3/D4/RN8.1/INV6): the field Sun now publishes as its own, additive 'result'
      // ('OK' | 'NOK' | empty). Painted exactly as received, never conditioned on 'status' -- a
      // WORKFLOW_PAUSED or FORECAST row still shows its result (S34, R11). Replaces the old
      // commented-out column, which read quality out of 'ok'/'status' instead of the new field.
      name: t_('Result'),
      field: 'result',
      readOnly: true,
      render: (part) => partResultLabel(part.result, t_)
    },
    {
      name: t_('Description'),
      field: 'description',
      charLimit: 200,
      hide: hideColumns
    },
    {
      name: t_('Raw Material Reference'),
      field: 'rawMaterialReference.code',
      type: 'select',
      edit: {
        source: async () => {
          return await apiGet('rawMaterialsReference')
        },
        id: 'id',
        field: 'code'
      },
      mandatory: true,
      condition: (rowData, column) => {
        return checkDimensionCondition(rowData, column)
      }
    },
    {
      name: t_('Fixture'),
      field: 'fixture.code',
      type: 'select',
      edit: {
        source: async () => {
          if (currentEditingRow !== null) { return await apiGet('fixturesAvailableByPartReference/' + currentEditingRow.id) }
        },
        id: 'id',
        field: 'code'
      }
    },
    {
      name: t_('Raw Material'),
      field: 'rawMaterial.name',
      render: (row) => {
        if (!row.workflowStarted && currentEditingRow !== null && currentEditingRow.id === row.id) {
          return (
            <>
              <TextField
                select
                variant='outlined'
                style={{ minWidth: '6em' }}
                InputLabelProps={{
                  shrink: true
                }}
                key={
                  'select' +
                  currentEditingRow.id +
                  '_raw_material'
                }
                value={editingRawMaterialId || 'not-available'}
                onChange={(event) => {
                  if (event.target.value === 'not-available') {
                    setEditingRawMaterialId(null)
                    row.rawMaterial = null
                  } else {
                    setEditingRawMaterialId(event.target.value)
                  }
                }}
              >
                {row.rawMaterial ? <MenuItem value={row.rawMaterial.id}>{row.rawMaterial.code}</MenuItem> : <MenuItem key='raw-material-not-available' value='not-available'>{t_('Not available')}</MenuItem>}
                {availableRawMaterials.map((item) => <MenuItem key={'raw-material-id' + item.id} value={item.id}>{item.code}</MenuItem>)}
              </TextField><FormHelperText> </FormHelperText>
            </>
          )
        } else {
          return row.rawMaterial ? row.rawMaterial.code : <Typography color='error'>{t_('Not available')}</Typography>
        }
      }
    },
    {
      name: t_('Location'),
      field: 'location.name',
      readOnly: true,
      hide: hideColumns
    },
    {
      name: t_('Shelf'),
      field: 'shelveStorageConfiguration.shelve',
      readOnly: true,
      hide: hideColumns
    },
    {
      name: t_('Row'),
      field: 'shelveStoragePosition.position',
      readOnly: true,
      hide: hideColumns
    },
    {
      name: t_('Column'),
      field: 'shelveStoragePosition.depth',
      readOnly: true,
      hide: hideColumns
    },
    {
      name: t_('Client code'),
      field: 'client.code',
      type: 'select',
      edit: {
        source: async () => {
          return await apiGet('clientsBasicData')
        },
        id: 'id',
        field: 'code'

      }
    },
    {
      name: t_('Client name'),
      field: 'client.name',
      type: 'select',
      edit: {
        source: async () => {
          return await apiGet('clientsBasicData')
        },
        id: 'id',
        field: 'name'
      }
    },
    {
      name: t_('Fabrication Order'),
      field: 'fabricationOrder.fabricationOrder',
      type: 'smartSelect',
      edit: {
        source: async () => {
          return await apiGet('fabricationOrderByPartReference/' + partReference.id)
        },
        id: 'id',
        field: 'fabricationOrder'
      }
    }

  ]

  const sortOptions = {
    default: {
      field: 'code',
      direction: partReferenceFilter ? -1 : 1
    }
  }

  const forceReloadAvailableMaterials = () => {
    setReloadAvailableMaterials(!reloadAvailableMaterials)
  }

  const actions = {
    edit: {
      fetchFunction: (row) => {
        if (row.location === null || row.location === undefined || ((Object.keys(row.location).length === 0 && Object.getPrototypeOf(row.location) === Object.prototype))) {
          row.shelveStoragePosition = null
        } else {
          row.shelveStoragePosition = row.depth
        }

        // Set raw material
        row.rawMaterial = editingRawMaterialId ? { id: editingRawMaterialId } : null

        // V5/E3 (S24): defensive guard. The Save button is already disabled via
        // forceDisableSaveButton while this holds, but the request must never go out even if this
        // function were ever reached some other way. Decided on `editingStatus` (S64): on a paused
        // FINISHED row `row.status` reads "WORKFLOW_PAUSED", so this guard would never fire.
        if (editingStatus === 'FINISHED' && (editingResult === null || editingResult === '')) {
          return Promise.resolve()
        }

        // RN19.3/V9 (S65/S66): the status the request carries is always the catalogue member the
        // dropdown holds, whether or not the user touched it — never `row.status`, which for a
        // paused or forecast part is a pseudo-state Sun rejects (E2), so an edit that only renamed
        // such a part used to fail. Assigned onto a payload copy rather than onto the row: the
        // Status column keeps reading the presentation status (RN19.4/S67), so saving must not
        // overwrite it locally.
        const payload = { ...row, status: editingStatus }

        // RN2/RN5/RN17 (R10): the result only exists for a row that ends up FINISHED. Leaving
        // FINISHED must OMIT the conformity field from the request, never send it as `false` — Sun's
        // mapper copies whatever `ok` arrives unconditionally, so a `false` would silently mark a
        // WORKING part as non-conforming (the defect RN17 exists to prevent).
        if (editingStatus === 'FINISHED') {
          payload.ok = editingResult === PART_RESULT_OK
        } else {
          delete payload.ok
        }

        return apiPut('parts', payload).then(forceReloadAvailableMaterials)
      },
      disabled: !isUserAuthorized('EDIT_PART_INSTANCE')
    },
    create: {
      fetchFunction: null,
      render: (extraFunctions) => {
        return (
          hideAddRecord
            ? null
            : <AddElementPart
                title={t_('ADD RECORD')}
                disable={!isUserAuthorized('ADD_PART_INSTANCE')}
                endpoint='parts'
                referenceName='Parts'
                partReference={partReference}
                onAddDone={forceReloadAvailableMaterials}
                onAlert={extraFunctions.handleShowAlert}
              />
        )
      }
    },
    delete: partReferenceFilter
      ? undefined
      : {
          fetchFunction: (rows) => {
            if (rows.length === 1) {
              return apiDelete('parts/' + rows[0].id).then(forceReloadAvailableMaterials)
            } else {
              return apiDelete('partsMulti', rows).then(forceReloadAvailableMaterials)
            }
          },
          disabled: !isUserAuthorized('DELETE_PART_INSTANCE')
        },
    custom: [
      {
        name: 'Documents',
        render: (row) => {
          return (
            <IconButton>
              <TextSnippetOutlined />
            </IconButton>
          )
        },
        disabled: !isUserAuthorized('DOCUMENTS_PART'),
        onClick: (row) => {
          setSelectedRow(row)
          setShowDocument(true)
        }
      },
      {
        name: t_('View history'),
        render: (row) => {
          return (
            <IconButton>
              <WorkHistoryIcon />
            </IconButton>
          )
        },
        disabled: !isUserAuthorized('PART_INSTANCE_HISTORY'),
        onClick: (row) => {
          setSelectedRow(row)
          setShowPartHistory(true)
        }
      },
      {
        name: 'View features',
        render: (row) => {
          return (
            <IconButton>
              <SquareFootIcon />
            </IconButton>
          )
        },
        disabled: true,
        onClick: (row) => {
          setSelectedRow(row)
          setShowPartFeatures(true)
        }
      },
      {
        name: 'Preset',
        render: (row) => {
          return (
            <IconButton>
              <Tune />
            </IconButton>
          )
        },
        disabled: !isUserAuthorized('FEATURE_PART_INSTANCE_PRESET'),
        onClick: (row) => {
          setSelectedRow(row)
          setShowPreset(true)
        }
      },
      ...(showQrAction
        ? [{
            name: 'Generate QR',
            render: (row) => (
              <IconButton>
                <QrCode2Icon />
              </IconButton>
            ),
            disabled: !isUserAuthorized('GENERATE_QR_CODE'),
            onClick: (row) => {
              setSelectedRow(row)
              setShowQr(true)
            }
          }]
        : [])
    ]
  }

  const onEditIndexChanged = (editIndex, row) => {
    setEditingRawMaterialId((row && row.rawMaterial) ? row.rawMaterial.id : null)
    // RN18/RN19/INV6.1 (S63/S64): every decision of the edit path is taken with the part's
    // PERSISTED status — the additive `persistedStatus` Sun publishes next to `status` (RN18) —
    // and never with the presentation status, which may carry a pseudo-state (WORKFLOW_PAUSED /
    // FORECAST) that is not a member of the catalogue. That covers which status the dropdown opens
    // on (RN19.1) and both whether the Result dropdown shows at all and on which value (RN19.2):
    // RN7 still says a row that is already FINISHED opens on the result it actually holds.
    // Deliberately no fallback to `row.status`: RN18.2 guarantees the field on this view (the parts
    // table is the only one with an edit path), and falling back is the exact vector that would
    // reintroduce defect O3 for paused rows only — invisible everywhere else.
    if (row && editIndex >= 0) {
      const persistedStatus = row.persistedStatus || ''
      setEditingStatus(persistedStatus)
      setEditingResult(persistedStatus === 'FINISHED' ? (row.ok ? PART_RESULT_OK : PART_RESULT_NOK) : '')
    } else {
      setEditingStatus(null)
      setEditingResult('')
    }
    setCurrentEditingRow(row || null)
    if (editIndex >= 0) {
      setCurrentEditingRow(row)
    }
  }

  const handleToggleClick = (toggleButton, filterName) => {
    const copyFilter = [...filters]
    const toggleButtonResult = toggleButton === null ? t_('NONE') : toggleButton

    copyFilter.find(filter => filter.name === filterName).key = toggleButtonResult
    setFilters(copyFilter)
  }

  const multipleSelectionActions = [
    {
      field: 'pause',
      render: (datafetch, index, pageable, multipleSelectionValue) => {
        return (
          <Button
            variant='outlined'
            sx={{ borderColor: 'transparent' }}
            color='warning'
            key={'key' + index}
            onClick={async () => {
              const pagination = { ...pageable }
              pagination.size = -1
              pagination.page = 0
              const data = await apiPut('part/pauseMulti/' + partReference.id, pagination)
              showAlert({
                title: t_('Part(s) has been paused'),
                severity: 'success'
              })
              toogleResetMultipleSelectionValue()
              if (data.length > 0) {
                const finishedParts = []
                const partsOnDevice = []
                // Separate different data
                data.forEach(element => {
                  if (element.status === 'FINISHED') {
                    finishedParts.push(element)
                  } else {
                    partsOnDevice.push(element)
                  }
                })
                setUnfinishedPartsPauseResume(finishedParts)
                setPartsOnDevicesToPause(partsOnDevice)
              }
            }}
          >
            <PauseCircle sx={{ marginRight: '0.5em' }} color='warning' />
            <Typography>{t_('Pause workflow')}</Typography>
          </Button>
        )
      }
    },
    {
      field: 'resume',
      render: (datafetch, index, pageable, multipleSelectionValue) => {
        return (
          <Button
            variant='outlined'
            sx={{ borderColor: 'transparent' }}
            color='success'
            key={'key' + index}
            onClick={async () => {
              const pagination = { ...pageable }
              pagination.size = -1
              pagination.page = 0
              const data = await apiPut('part/resumeMulti/' + partReference.id, pagination)
              showAlert({
                title: t_('Part(s) has been resumed'),
                severity: 'success'
              })
              toogleResetMultipleSelectionValue()
              if (data.length > 0) {
                setUnfinishedPartsPauseResume(data)
              }
            }}
          >
            <PlayCircleOutlineIcon sx={{ marginRight: '0.5em' }} color='success' />
            <Typography>{t_('Resume workflow')}</Typography>
          </Button>
        )
      }
    }
  ]

  const onPauseWorkflowForce = async (partId, endJob) => {
    partsOnDevicesToPause.shift()
    setPartsOnDevicesToPause([...partsOnDevicesToPause])
    await apiPut('part/pause/' + partId + '/' + endJob)
  }

  return (
    <>
      {AlertElement}
      <PausePartSelectEndJobDialog
        setOpen={unfinishedPartsPauseResume.length === 0 && partsOnDevicesToPause.length > 0 ? partsOnDevicesToPause[0].id : false}
        cancelCallback={() => {
          partsOnDevicesToPause.shift()
          setPartsOnDevicesToPause([...partsOnDevicesToPause])
        }}
        part={partsOnDevicesToPause.length > 0 ? partsOnDevicesToPause[0] : null}
        pauseNowCallback={() => { onPauseWorkflowForce(partsOnDevicesToPause[0].id, false) }}
        pauseAtJobEndCallback={() => { onPauseWorkflowForce(partsOnDevicesToPause[0].id, true) }}
      />
      <PauseResumeFinishedList
        setOpen={unfinishedPartsPauseResume.length > 0}
        parts={unfinishedPartsPauseResume}
        closeCallback={() => setUnfinishedPartsPauseResume([])}
      />
      {selectedRow && selectedRow.id && (
        <SmartDialog
          closeCallback={() => setShowDocument(false)}
          accept={false}
          cancel={false}
          close
          disableButtons={isDownloadingFile}
          renderComponent={
            <div style={accordionStyle}>
              <DefaultAccordion headerRender={t_('Part')}>
                <Document reference={selectedRow} resourceType='PART' isDownloadingFile={isDownloadingFile} setIsDownloadingFile={setIsDownloadingFile} />
              </DefaultAccordion>
              <DefaultAccordion headerRender={t_('Part reference')} defaultExpanded={false}>
                <Document reference={selectedRow?.partReference ? selectedRow.partReference : null} resourceType='PART_REFERENCE' isDownloadingFile={isDownloadingFile} setIsDownloadingFile={setIsDownloadingFile} />
              </DefaultAccordion>
              <DefaultAccordion headerRender={t_('Fabrication Order')} defaultExpanded={false} disabled={!selectedRow?.fabricationOrder}>
                <Document reference={selectedRow?.fabricationOrder ? { id: selectedRow.fabricationOrder.id, code: selectedRow.fabricationOrder.fabricationOrder, name: selectedRow.fabricationOrder.fabricationOrder } : null} resourceType='FABRICATION_ORDER' isDownloadingFile={isDownloadingFile} setIsDownloadingFile={setIsDownloadingFile} />
              </DefaultAccordion>
              <DefaultAccordion headerRender={t_('Job')} defaultExpanded={false} disabled={!selectedRow?.fabricationOrder}>
                <SmartMateriaUITable
                  columns={[{ name: t_('Name'), field: 'name', readOnly: true }, { name: t_('Job Linker'), field: 'jobLinker', readOnly: true }, { name: t_('Fabrication Order'), field: 'fabricationOrderName', readOnly: true }]}
                  dataFetch={selectedRow?.fabricationOrder?.linkedJobs ? selectedRow.fabricationOrder.linkedJobs : []}
                  actions={{
                    custom: [{
                      name: 'Documents',
                      render: (row) => {
                        return (
                          <IconButton>
                            <TextSnippetOutlined />
                          </IconButton>
                        )
                      },
                      onClick: (row) => {
                        setSelectedJob(row)
                        setShowJobDocument(true)
                      }
                    }]
                  }}
                />
              </DefaultAccordion>
            </div>
          }
          setOpen={showDocument}
        />
      )}
      <SmartDialog
        renderComponent={<PartHistory partId={selectedRow.id} />}
        setOpen={showPartHistory}
        cancel={false}
        accept={false}
        close
        closeCallback={() => { setShowPartHistory(false) }}
      />
      <SmartDialog
        renderComponent={<PartFeature part={selectedRow} />}
        setOpen={showPartFeatures}
        cancel={false}
        accept={false}
        close
        closeCallback={() => { setShowPartFeatures(false) }}
      />
      <SmartDialog
        renderComponent={<PartPreset part={selectedRow} />}
        setOpen={showPreset}
        cancel={false}
        accept={false}
        close
        title={{ render: <Typography variant='h5'>{t_('Preset') + ': ' + (selectedRow.name || '')}</Typography> }}
        closeCallback={() => { setShowPreset(false) }}
      />
      {showQr && (
        <QrCodeGeneratorDialog
          open={showQr}
          onClose={() => setShowQr(false)}
          entityType='Part'
          entityLabel={t_('Part')}
          instance={selectedRow}
          availableFields={columns
            .filter(c => c.field)
            .map(c => ({ field: c.field, label: c.name, type: c.type }))
            .filter((f, i, arr) => arr.findIndex(x => x.field === f.field) === i)}
        />
      )}
      <SmartMateriaUITable
        tableTuneUniqueKey='part'
        columns={columns}
        title={t_('Parts')}
        dataFetch={getTableData}
        actions={actions}
        preselectedRecord={preselectedRecord}
        sortOptions={sortOptions}
        multipleSelection={!partReferenceFilter}
        multipleSelectionActions={multipleSelectionActions}
        resetMultipleSelectionValue={resetMultipleSelectionValue}
        onRowClick={(row) => {
          if (setSelectedPart && typeof setSelectedPart === 'function' && row.id) {
            setSelectedPart(row)
          }
        }}
        forceLoad={forceLoad}
        editingRow={(event) => {
          if (setEditingRow && typeof setEditingRow === 'function') {
            setEditingRow(event)
          }
        }}
        onEditIndexChanged={onEditIndexChanged}
        // V5/E3 (S24): a FINISHED row with no Result chosen cannot be confirmed — same defensive
        // rule as the creation dialog (AddElementPart), applied here to the edit action.
        forceDisableSaveButton={editingStatus === 'FINISHED' && (editingResult === null || editingResult === '')}
        pageable
        filters={filters}
        onFilterToggleClick={(toggleButton, filterName) => handleToggleClick(toggleButton, filterName)}
        extraFilterParameter={{ name: 'disabled', value: true }}

      />
      <SmartDialog
        closeCallback={() => setShowJobDocument(false)}
        accept={false}
        cancel={false}
        close
        disableButtons={isDownloadingFile}
        renderComponent={<Document reference={selectedJob} resourceType='JOB' isDownloadingFile={isDownloadingFile} setIsDownloadingFile={setIsDownloadingFile} />}
        setOpen={showJobDocument}
      />
    </>
  )
}

export default Parts
